import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Typography, Box, Paper, CircularProgress } from '@mui/material';
import api from '../services/api';
import KriticCard from './KriticCard';
import StarIcon from '@mui/icons-material/Star';

const Section = styled.div`
  margin-top: 30px;
  margin-bottom: 30px;
`;

const TopKriticsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-top: 15px;
`;

// Scaled down card wrapper for home page
const ScaledCardWrapper = styled.div`
  transform: scale(0.5);
  transform-origin: top left;
  height: 125px; // 0.5 * 250px
  width: 200%; // To maintain aspect ratio inside the transform
`;

const TopKriticsHome = ({ user, lastUpdate }) => {
    const [top5, setTop5] = useState([]);
    const [maxMatchScore, setMaxMatchScore] = useState(0);
    const [loading, setLoading] = useState(true);
    const userId = user?.id;

    useEffect(() => {
        const fetchTopKritics = async () => {
            if (!userId) {
                setLoading(false);
                return;
            }

            try {
                // Don't set loading to true for background updates to avoid flicker
                // setLoading(true); 
                // Actually, for the very first load we want loading.
                // But for "triggerRefresh" updates we might want silent updates or strict updates.
                // Let's keep simpler logic for now: if explicit refresh, maybe silent?
                // The existing code sets loading=true inside.

                // Let's rely on standard flow.
                const data = await api.getMatchingKritics(userId);

                if (data && data.length > 0) {
                    const max = Math.max(...data.map(k => k.match_score));
                    setMaxMatchScore(max);
                    setTop5(data.slice(0, 5));
                } else {
                    setTop5([]);
                }
            } catch (err) {
                console.error("Failed to fetch top kritics:", err);
                // Don't set error state here to avoid breaking UI on transient errors during refresh
                // setError(err.message); 
            } finally {
                setLoading(false);
            }
        };

        fetchTopKritics();
    }, [userId, lastUpdate]);

    if (!user) {
        return (
            <Section>
                <Typography variant="h6" gutterBottom>Find Your Kritic Matches</Typography>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f9f9fb' }}>
                    <Typography variant="body1">
                        <strong>Log in</strong> to discover critics with similar tastes and get personalized movie recommendations!
                    </Typography>
                </Paper>
            </Section>
        );
    }

    if (loading) {
        return (
            <Section>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size={30} />
                </Box>
            </Section>
        );
    }

    const PlaceholderCard = styled.div`
      background-color: #f0f0f0;
      border-radius: 12px;
      height: 125px;
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      position: relative;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0,0,0,0.05);

      &::before {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background: linear-gradient(to top, rgba(0,0,0,0.4), transparent);
        z-index: 1;
      }
    `;

    const PlaceholderName = styled.div`
      height: 16px;
      width: 70%;
      background-color: rgba(255,255,255,0.3);
      margin: 10px;
      border-radius: 4px;
      z-index: 2;
    `;

    if (top5.length === 0) {
        return (
            <Section>
                <Typography variant="h6" gutterBottom color="text.secondary">Top Kritic Matches</Typography>
                <TopKriticsGrid sx={{ opacity: 0.4, pointerEvents: 'none', filter: 'grayscale(100%)' }}>
                    {[...Array(5)].map((_, i) => (
                        <PlaceholderCard key={i}>
                            <PlaceholderName />
                        </PlaceholderCard>
                    ))}
                </TopKriticsGrid>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center', color: 'text.secondary' }}>
                    Rate more movies to unlock your matches
                </Typography>
            </Section>
        );
    }

    return (
        <Section>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon sx={{ color: 'gold', fontSize: '1.2rem' }} /> Your Top 5 Kritic Matches
            </Typography>
            <TopKriticsGrid>
                {top5.map(critic => (
                    <Box key={critic.site_identifier} sx={{ width: '100%', height: '125px' }}>
                        <ScaledCardWrapper>
                            <KriticCard critic={critic} maxMatchScore={maxMatchScore} />
                        </ScaledCardWrapper>
                    </Box>
                ))}
            </TopKriticsGrid>
        </Section>
    );
};

export default TopKriticsHome;
